/**
 * Utility functions for error handling and detection
 */
import { translateRawRuntime } from "@/lib/i18n/rawTranslations";

export interface ErrorInfo {
  type: "network" | "firewall" | "auth" | "server" | "unknown";
  message: string;
  description: string;
}

/**
 * Detects the type of error and returns appropriate error information
 */
export function detectErrorType(error: any): ErrorInfo {
  // Check for network/firewall errors
  if (error?.status === "FETCH_ERROR" || error?.status === "TIMEOUT_ERROR") {
    return {
      type: "firewall",
      message: "Connection Blocked",
      description:
        "Your request was blocked by a firewall or network security policy. Please contact your network administrator or try from a different network.",
    };
  }

  // Check for specific HTTP status codes that indicate firewall/network issues
  if (error?.status === 0 || error?.status === "TIMEOUT_ERROR") {
    return {
      type: "firewall",
      message: "Connection Blocked",
      description:
        "Your request was blocked by a firewall or network security policy. Please contact your network administrator or try from a different network.",
    };
  }

  // Check for server errors (5xx)
  if (error?.status >= 500 && error?.status < 600) {
    return {
      type: "server",
      message: "Server Error",
      description: "The server is experiencing issues. Please try again later.",
    };
  }

  // Check for authentication errors (401, 403)
  if (error?.status === 401 || error?.status === 403) {
    return {
      type: "auth",
      message: "Authentication Failed",
      description: "Please check your credentials and try again.",
    };
  }

  // Check for network error messages in the error object
  if (error?.message) {
    const message = error.message.toLowerCase();

    if (
      message.includes("network error") ||
      message.includes("connection refused") ||
      message.includes("timeout") ||
      message.includes("econnrefused") ||
      message.includes("enotfound") ||
      message.includes("fetch failed")
    ) {
      return {
        type: "network",
        message: "Network Error",
        description:
          "Unable to connect to the server. Please check your internet connection and try again.",
      };
    }

    if (
      message.includes("firewall") ||
      message.includes("blocked") ||
      message.includes("forbidden") ||
      message.includes("access denied")
    ) {
      return {
        type: "firewall",
        message: "Access Blocked",
        description:
          "Your request was blocked by a firewall or network security policy. Please contact your network administrator or try from a different network.",
      };
    }
  }

  // Default to authentication error for unknown cases
  return {
    type: "auth",
    message: "Login Failed",
    description: "Please check your credentials and try again.",
  };
}

/**
 * Gets user-friendly error message based on error type
 */
export function getErrorMessage(error: any): {
  message: string;
  description: string;
} {
  const errorInfo = detectErrorType(error);
  return {
    message: translateRawRuntime(errorInfo.message),
    description: translateRawRuntime(errorInfo.description),
  };
}
